import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { BRANDS } from "../config/brands";

export default function BrandCards({ onComingSoon }) {
    return (
        <section className="mx-auto max-w-6xl px-5 py-16 lg:hidden" aria-label="Brand directory">
            <p className="eyebrow mb-3 text-center">The Family of Brands</p>
            <h2 className="font-display mb-10 text-center text-2xl font-medium tracking-tight text-slate-100 sm:text-3xl">
                Six Brands. <span className="text-gold-gradient">One Umbrella.</span>
            </h2>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {BRANDS.map((brand, i) => {
                    const isActive = brand.status === "active";
                    return (
                        <motion.div
                            key={brand.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.7, delay: (i % 2) * 0.1, ease: [0.22, 1, 0.36, 1] }}
                            className="relative overflow-hidden rounded-2xl border border-white/5 bg-[#0A0B10]/80 p-5 backdrop-blur-sm"
                            style={{ boxShadow: `inset 0 1px 0 rgba(255,255,255,0.04), 0 0 0 1px ${brand.accent}14` }}
                            data-testid={`teg-brand-card-${brand.id}`}
                        >
                            <div
                                className="absolute inset-x-0 top-0 h-[2px]"
                                style={{ background: `linear-gradient(90deg, transparent, ${brand.accent}, transparent)` }}
                                aria-hidden="true"
                            />
                            <div className="flex items-start gap-4">
                                <img
                                    src={brand.logo}
                                    alt={`${brand.name} logo`}
                                    loading="lazy"
                                    className={
                                        brand.logoShape === "wide"
                                            ? "h-14 w-auto max-w-[130px] shrink-0 rounded-lg object-contain"
                                            : "h-14 w-14 shrink-0 rounded-full object-cover"
                                    }
                                    style={{ boxShadow: `0 0 24px ${brand.accent}45, 0 0 0 1px ${brand.accent}55` }}
                                />
                                <div className="min-w-0 flex-1">
                                    <p className="font-mono-lux text-[9px] tracking-[0.25em] uppercase" style={{ color: `${brand.accent}CC` }}>
                                        {brand.eyebrow}
                                    </p>
                                    <h3 className="font-display mt-1 text-lg font-medium text-slate-100">
                                        {brand.id === "truth-insurance" ? (
                                            <a
                                                href={brand.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="transition-colors hover:text-amber-300"
                                                aria-label="Truth Insurance — Visit Website (opens in a new tab)"
                                            >
                                                {brand.name}
                                            </a>
                                        ) : (
                                            brand.name
                                        )}
                                    </h3>
                                    <p className="mt-1 text-sm leading-relaxed text-slate-400">{brand.description}</p>
                                </div>
                            </div>
                            <div className="mt-4">
                                {isActive ? (
                                    <a
                                        href={brand.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full border border-amber-400/40 px-5 text-[11px] font-medium tracking-[0.22em] text-amber-300 uppercase transition-all duration-300 hover:bg-amber-400/10 hover:shadow-[0_0_24px_rgba(212,175,55,0.3)]"
                                        aria-label={`${brand.name} — Visit Website (opens in a new tab)`}
                                        data-testid={`teg-brand-card-cta-${brand.id}`}
                                    >
                                        Visit Website <ArrowUpRight size={14} />
                                    </a>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => onComingSoon(brand)}
                                        className="inline-flex min-h-[44px] w-full items-center justify-center rounded-full border border-blue-400/30 px-5 text-[11px] font-medium tracking-[0.22em] text-blue-300 uppercase transition-all duration-300 hover:bg-blue-400/10"
                                        aria-label={`${brand.name} — Coming Soon`}
                                        data-testid={`teg-brand-card-cta-${brand.id}`}
                                    >
                                        Coming Soon
                                    </button>
                                )}
                                {brand.bookingUrl && (
                                    <a
                                        href={brand.bookingUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-2 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full border border-amber-400/50 bg-[#060913] px-5 text-[11px] font-medium tracking-[0.22em] text-amber-200/90 uppercase transition-all duration-300 hover:bg-amber-400/10 hover:shadow-[0_0_24px_rgba(212,175,55,0.35)]"
                                        aria-label={`${brand.name} — Book a Call (opens in a new tab)`}
                                        data-testid="teg-book-call-card-btn"
                                    >
                                        Book a Call
                                    </a>
                                )}
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </section>
    );
}
